import Accordion from "components/Accordion";
import React, { useState, useRef, useContext } from "react";
import { Button } from "react-bootstrap";
import ErrorHandler from "components/core/ErrorHandler";
import { Row, Col } from "react-bootstrap";
import { GridColumn } from "@progress/kendo-react-grid";

import AdminPaymentMatchingForm from "./AdminPaymentMatchingForm";
import { useMatchClaimsAndPayments } from "./hooks/useMatchClaimsAndPayments";
import { useUnmatchClaimsAndPayments } from "./hooks/useUnmatchClaimsAndPayments";

import SharedMainSelectableClientGrid from "shared/components/grids/clientGrid/SharedMainSelectableClientGrid";
import { GetDataWithCleanedDates } from "shared/helpers/DateHelpers";
import { DetailsModal } from "components/grid/DetailsModal";
import { ZIndexContext } from "@progress/kendo-react-common";
import { useReactQueryHelpers } from "shared/hooks/useReactQueryHelpers";

import {
    DefaultColumnWidth,
    DefaultNoFilterColumnWidth,
    DefaultWideColumnWidth
} from "components/grid/Kendo/KendoGridAndColumnConstants";
import { UserContext } from "shared/context/UserContext";
import { LoadingSpinner } from "components/layout/LoadingSpinner";

import { MoneyCell } from "components/grid/Kendo/CustomKendoGridCells";

export default function AdminPaymentMatching(props) {
    const { passedProps } = props;
    const userConfig = useContext(UserContext);

    const _container = React.useRef();
    const claimListingGridRef = useRef(null);
    const paymentListingGridRef = useRef(null);
    const matchingHistoryDetailsGridRef = useRef(null);

    const [isLoading, setIsLoading] = useState(false);

    const [mainGridClientGridApiParams, setMainGridClientGridApiParams] = useState(null);
    const [matchingHistoryDetailsGridApiParams, setMatchingHistoryDetailsGridApiParams] = useState(null);
    const [matchingHistoryDetailsGridApiEndpoint, setMatchingHistoryDetailsGridApiEndpoint] = useState(null);

    const [showHistoryModal, setShowHistoryModal] = useState(false);

    let defaultColumnWidth = DefaultColumnWidth();
    let defaultWideColumnWidth = DefaultWideColumnWidth();

    let { mutate: mutateForMatchClaimsAndPayments } = useMatchClaimsAndPayments();
    let { mutate: mutateForUnmatchClaimsAndPayments } = useUnmatchClaimsAndPayments();
    const { invalidateReactQueryKey } = useReactQueryHelpers();

    function handleFormSubmit(formValues) {
        // console.log(formValues);
        setMainGridClientGridApiParams(formValues);
    }

    function handleFormReset(e) {
        // setMainGridData([]);
        console.log(e);
        setMainGridClientGridApiParams(null);
    }

    function handleUnmatchClick(e) {
        let parameters = prepareMatchingParameters(false, true);

        if (parameters) {
            if (getSelectedRowsCountAndConfirmAction(false)) {
                setIsLoading(true);
                mutateForUnmatchClaimsAndPayments(parameters);
            }
        }
    }


    // function handleMatchClick(e) {
    //     //NOTE: Need to clear out the selected grid rows

    //     claimListingGridRef.current.UnselectAllRows();
    //     paymentListingGridRef.current.UnselectAllRows();
    // }

    //NOTE TODO: Restore this logic after I figure out how to clear out the selected grid rows
    function handleMatchClick(e) {
        let parameters = prepareMatchingParameters(true, false);

        if (parameters) {
            if (getSelectedRowsCountAndConfirmAction()) {
                setIsLoading(true);
                mutateForMatchClaimsAndPayments(parameters);
            }
        }
    }

    function getSelectedRowsCountAndConfirmAction(isMatch = true) {
        const selectedClaims = claimListingGridRef.current
            ? claimListingGridRef.current.GetSelectedGridData()
            : [];

        const selectedPayments = paymentListingGridRef.current
            ? paymentListingGridRef.current.GetSelectedGridData()
            : [];

        // console.log(selectedClaims);

        let count = selectedClaims.length + selectedPayments.length;
        let action = isMatch ? "match" : "unmatch";

        return window.confirm("You are about to " + action + " " + count + " claims/payment(s). Press OK to Continue.");
    }

    function prepareMatchingParameters(matchCheck = false, unmatchCheck = false) {
        const selectedClaims = claimListingGridRef.current
            ? claimListingGridRef.current.GetSelectedGridData()
            : [];

        const selectedPayments = paymentListingGridRef.current
            ? paymentListingGridRef.current.GetSelectedGridData()
            : [];

        if (matchCheck && (!selectedClaims || selectedClaims.length === 0)) {
            alert("Please select at least one claim.");
            return null;
        }

        if (matchCheck && (!selectedPayments || selectedPayments.length === 0)) {
            alert("Please select at least one payment.");
            return null;
        }

        if (matchCheck && selectedClaims && selectedClaims.length > 1) {
            alert("You can't match multiple claims.");
            return null;
        }

        if (unmatchCheck && (!selectedClaims || selectedClaims.length === 0)) {
            alert("Please select at least one claim/payment to unmatch.");
            return null;
        }

        const pyids = selectedPayments.map(payment => payment.pyid).join(",");
        const clids = selectedClaims.map(claim => claim.clid).join(",");

        //NOTE: I need clidsAndPmids to be a comma-separated string consisting of the selected rows for the claims grid like this: "clid1|pmid1,clim2|pmid2"
        const clidsAndPmids = selectedClaims.map(claim => `${claim.clid}|${claim.pmid}`).join(",");
        // console.log(clidsAndPmids);

        let parameters = {
            user: userConfig.email,
            rpid: mainGridClientGridApiParams.rpid,
            pyids: pyids,
            // clids: clids
        }

        if (matchCheck) {
            parameters.clids = clids;
            parameters.callback = () => {
                invalidateReactQueryKey("api/AdminPaymentMatching/GetClaimMatchingHistory");
                invalidateReactQueryKey("api/AdminPaymentMatching/GetPaymentMatchingHistory");
                invalidateReactQueryKey("api/AdminPaymentMatching/GetClaimsForMatching");
                invalidateReactQueryKey("api/AdminPaymentMatching/GetPaymentsForMatching");
                setIsLoading(false);

                claimListingGridRef.current.UnselectAllRows();
                paymentListingGridRef.current.UnselectAllRows();

                alert("Selected payment(s) are matched with claims successfully.");
            }
        }
        else if (unmatchCheck) {
            parameters.clids = clidsAndPmids;

            parameters.callback = () => {
                invalidateReactQueryKey("api/AdminPaymentMatching/GetClaimMatchingHistory");
                invalidateReactQueryKey("api/AdminPaymentMatching/GetPaymentMatchingHistory");
                invalidateReactQueryKey("api/AdminPaymentMatching/GetClaimsForMatching");
                invalidateReactQueryKey("api/AdminPaymentMatching/GetPaymentsForMatching");
                setIsLoading(false);

                claimListingGridRef.current.UnselectAllRows();
                paymentListingGridRef.current.UnselectAllRows();

                alert("Selected payment/claim(s) are unmatched successfully.");
            }

        }
        //NOTE: I don't think this else condition is hit, but I'm keeping it for safety
        else {
            parameters.callback = () => {
                invalidateReactQueryKey("api/AdminPaymentMatching/GetClaimMatchingHistory");
                invalidateReactQueryKey("api/AdminPaymentMatching/GetPaymentMatchingHistory");
                invalidateReactQueryKey("api/AdminPaymentMatching/GetClaimsForMatching");
                invalidateReactQueryKey("api/AdminPaymentMatching/GetPaymentsForMatching");
                setIsLoading(false);
            }
        }

        return parameters;
    }

    const HistoryCell = (props) => {
        async function viewClick(event) {
            if (props.dataItem.pyid) {
                console.log("this is a payment");

                setMatchingHistoryDetailsGridApiEndpoint("api/AdminPaymentMatching/GetPaymentMatchingHistory");
                setMatchingHistoryDetailsGridApiParams({
                    user: userConfig.email,
                    pyid: props.dataItem.pyid
                });
            }
            else {
                console.log("this is a claim");

                setMatchingHistoryDetailsGridApiEndpoint("api/AdminPaymentMatching/GetClaimMatchingHistory");
                setMatchingHistoryDetailsGridApiParams({
                    user: userConfig.email,
                    clid: props.dataItem.clid
                });

            }

            setShowHistoryModal(true);
        }

        return (
            <td className="noWrap" {...props.style}>
                <a
                    onClick={viewClick}
                    style={{
                        cursor: "pointer",
                        textDecoration: "underline",
                        color: "#015CAB",
                    }}
                >
                    <i className="bi bi-eye"></i>
                </a>
            </td>
        );
    };

    function requestsDataProcessor(responseData) {
        let dataWithCleanedDates = GetDataWithCleanedDates(responseData);
        return dataWithCleanedDates;
    };


    let detailsGridUseQueryOptions = { refetchOnWindowFocus: false };

    function matchingHistoryDetailsGridBasedOnClaimOrPayment() {
        if (!(matchingHistoryDetailsGridApiParams && matchingHistoryDetailsGridApiEndpoint)) {
            return null;
        }

        if (matchingHistoryDetailsGridApiParams.clid) {

            /*
                     { key: "NCPDP", headerText: "NCPDP", dataType: "string", width: "5%" },
            { key: "SCRIPT", headerText: "Rx #", dataType: "string", width: "6%" },
            { key: "TRANSDATE", headerText: "Transaction Date", dataType: "date", format: "MM/dd/yyyy", width: "8%" },
            { key: "PAYER", headerText: "Payer", dataType: "string", width: "12%" },
            { key: "BIN", headerText: "BIN", dataType: "string", width: "6%" },
            { key: "ADJ", headerText: "Adjudicated", dataType: "string", width: "8%" },
            { key: "AMOUNT", headerText: "Paid", dataType: "number", width: "6%" },
            { key: "CLID", headerText: "CLID", dataType: "number", width: "8%" },
            { key: "PREVIOUSLYMATCHED", headerText: "Previously Matched To (PMID)", dataType: "number", width: "14%" },
            { key: "TYPE", headerText: "Status", dataType: "string", width: "6%" },
            { key: "MODUSER", headerText: "Changed By", dataType: "string", width: "8%" },
            { key: "MODSTAMP", headerText: "Changed On", dataType: "date", format: "MM/dd/yyyy hh:mm:ss tt", width: "13%" }
 */
            return (
                <SharedMainSelectableClientGrid
                    ref={matchingHistoryDetailsGridRef}
                    apiGetEndpoint={matchingHistoryDetailsGridApiEndpoint}
                    apiParameters={matchingHistoryDetailsGridApiParams}
                    useQueryOptions={detailsGridUseQueryOptions}
                    //NOTE: If isSelectable is true but no dataItemKey is selected, then everything is selected
                    isSelectable={true}
                    dataItemKey={"clid"}
                    onDataRetrievedProcessor={requestsDataProcessor}
                >
                    <GridColumn
                        key={"ncpdp"}
                        field={"ncpdp"}
                        title={"NCPDP"}
                        width={defaultColumnWidth}
                    />
                    <GridColumn
                        key={"rxNumber"}
                        field={"rxNumber"}
                        title={"Rx #"}
                        filter={"text"}
                        width={defaultColumnWidth}
                    />
                    <GridColumn
                        key={"transactionDate"}
                        field={"transactionDate"}
                        title={"Transaction Date"}
                        width={defaultWideColumnWidth}
                        filter="date"
                        format="{0:d}"
                    />
                    <GridColumn
                        key={"bin"}
                        field={"bin"}
                        title={"BIN"}
                        width={defaultColumnWidth}
                    />
                    <GridColumn
                        key={"adjudicatedAmount"}
                        field={"adjudicatedAmount"}
                        title={"Adjudicated"}
                        width={defaultColumnWidth}
                        cells={{ data: MoneyCell }}
                    />
                    <GridColumn
                        key={"paidAmount"}
                        field={"paidAmount"}
                        title={"Paid"}
                        width={defaultColumnWidth}
                        cells={{ data: MoneyCell }}
                    />
                    <GridColumn
                        key={"clid"}
                        field={"clid"}
                        title={"CLID"}
                        width={defaultColumnWidth}
                    />
                    <GridColumn
                        key={"previouslyMatched"}
                        field={"previouslyMatched"}
                        title={"Previously Matched to (PMID)"}
                        width={defaultWideColumnWidth}
                    />
                    <GridColumn
                        key={"type"}
                        field={"type"}
                        title={"Status"}
                        width={defaultColumnWidth}
                    />
                    <GridColumn
                        key={"modUser"}
                        field={"modUser"}
                        title={"Changed By"}
                        width={defaultColumnWidth}
                    />
                    <GridColumn
                        key={"modStamp"}
                        field={"modStamp"}
                        title={"Changed On"}
                        width={defaultWideColumnWidth}
                        filter="date"
                    />
                </SharedMainSelectableClientGrid>
            )
        }
        else {
            /*
                                { key: "NCPDP", headerText: "NCPDP", dataType: "string", width: "5%" },
                    { key: "PMID", headerText: "PMID", dataType: "string", width: "8%" },
                    { key: "PAYER", headerText: "Payer", dataType: "string", width: "12%" },
                    { key: "CHECKDATE", headerText: "Check Date", dataType: "date", format: "MM/dd/yyyy", width: "8%" },
                    { key: "CHECKNO", headerText: "Check Number", dataType: "string", width: "9%" },
                    { key: "PTID", headerText: "Pament Type", dataType: "string", width: "7%" },
                    { key: "AMOUNT", headerText: "Paid", dataType: "number", width: "5%" },
                    { key: "CLID", headerText: "CLID", dataType: "number", width: "7%" },
                    { key: "PREVIOUSLYMATCHED", headerText: "Previously Matched To (CLID)", dataType: "number", width: "14%" },
                    { key: "TYPE", headerText: "Status", dataType: "string", width: "6%" },
                    { key: "MODUSER", headerText: "Changed By", dataType: "string", width: "8%" },
                    { key: "MODSTAMP", headerText: "Changed On", dataType: "date", format: "MM/dd/yyyy hh:mm:ss tt", width: "11%" }
            */

            return (
                <SharedMainSelectableClientGrid
                    ref={matchingHistoryDetailsGridRef}
                    apiGetEndpoint={matchingHistoryDetailsGridApiEndpoint}
                    apiParameters={matchingHistoryDetailsGridApiParams}
                    useQueryOptions={detailsGridUseQueryOptions}
                    //NOTE: If isSelectable is true but no dataItemKey is selected, then everything is selected
                    isSelectable={true}
                    dataItemKey={"pyid"}
                    onDataRetrievedProcessor={requestsDataProcessor}
                >
                    <GridColumn
                        key={"ncpdp"}
                        field={"ncpdp"}
                        title={"NCPDP"}
                        width={defaultColumnWidth}
                    />
                    <GridColumn
                        key={"pmid"}
                        field={"pmid"}
                        title={"PMID"}
                        filter={"text"}
                        width={defaultColumnWidth}
                    />
                    <GridColumn
                        key={"payer"}
                        field={"payer"}
                        title={"Payer"}
                        filter={"text"}
                        width={defaultWideColumnWidth}
                    />
                    <GridColumn
                        key={"checkDate"}
                        field={"checkDate"}
                        title={"Check Date"}
                        width={defaultWideColumnWidth}
                        filter="date"
                        format="{0:d}"
                    />
                    <GridColumn
                        key={"checkNumber"}
                        field={"checkNumber"}
                        title={"check Number"}
                        width={defaultColumnWidth}
                    />
                    <GridColumn
                        key={"paymentType"}
                        field={"paymentType"}
                        title={"Payment Type"}
                        width={defaultColumnWidth}
                    />
                    <GridColumn
                        key={"amount"}
                        field={"amount"}
                        title={"Paid"}
                        width={defaultColumnWidth}
                        filter={"numeric"}
                        cells={{ data: MoneyCell }}
                    />
                    <GridColumn
                        key={"clid"}
                        field={"clid"}
                        title={"Clid"}
                        width={defaultColumnWidth}
                        filter={"text"}
                    />
                    <GridColumn
                        key={"previouslyMatched"}
                        field={"previouslyMatched"}
                        title={"Previously Matched to (CLID)"}
                        width={defaultWideColumnWidth}
                    />
                    <GridColumn
                        key={"status"}
                        field={"status"}
                        title={"Status"}
                        width={defaultColumnWidth}
                    />
                    <GridColumn
                        key={"modUser"}
                        field={"modUser"}
                        title={"Changed By"}
                        width={defaultColumnWidth}
                    />
                    <GridColumn
                        key={"modStamp"}
                        field={"modStamp"}
                        title={"Changed On"}
                        width={defaultWideColumnWidth}
                        filter="date"
                    />
                </SharedMainSelectableClientGrid>
            )
        }
    }

    return (
        <div className="feature">
            <LoadingSpinner
                isDataLoading={isLoading}
                controlsName="AdminPaymentMatching"
            />
            <Accordion defaultExpanded label="Search & Filter">
                <Row>
                    <Col className="higher-zindex-filters">
                        <AdminPaymentMatchingForm
                            handleFormSubmit={handleFormSubmit}
                            passedProps={passedProps}
                            handleFormReset={handleFormReset}
                        />
                    </Col>
                </Row>
            </Accordion>
            <br />
            <div ref={_container}>
                <Row>
                    <Col>
                        <h4>Claim Listing</h4>
                        {mainGridClientGridApiParams ?
                            <Col className="tight-grid tight-grid multi-line-filter">
                                <SharedMainSelectableClientGrid
                                    ref={claimListingGridRef}
                                    styleOverride={{ height: "510px" }}
                                    // styleOverride={{ height: "1200px" }}
                                    apiGetEndpoint={"api/AdminPaymentMatching/GetClaimsForMatching"}
                                    apiParameters={mainGridClientGridApiParams}
                                    useQueryOptions={detailsGridUseQueryOptions}
                                    //NOTE: If isSelectable is true but no dataItemKey is selected, then everything is selected
                                    isSelectable={true}
                                    dataItemKey={"uniqueRowId"}
                                    onDataRetrievedProcessor={requestsDataProcessor}
                                >
                                    <GridColumn
                                        key={"ncpdp"}
                                        field={"ncpdp"}
                                        title={"NCPDP"}
                                        width={defaultColumnWidth}
                                    />
                                    <GridColumn
                                        key={"rxNumber"}
                                        field={"rxNumber"}
                                        title={"Rx #"}
                                        width={defaultColumnWidth}
                                    />
                                    <GridColumn
                                        key={"serviceDate"}
                                        field={"serviceDate"}
                                        title={"Service Date"}
                                        width={defaultWideColumnWidth}
                                        filter="date"
                                        format="{0:d}"
                                    />
                                    <GridColumn
                                        key={"transactionDate"}
                                        field={"transactionDate"}
                                        title={"Transaction Date"}
                                        width={defaultWideColumnWidth}
                                        filter="date"
                                        format="{0:d}"
                                    />
                                    <GridColumn
                                        key={"rxNumber"}
                                        field={"rxNumber"}
                                        title={"Rx #"}
                                        width={defaultColumnWidth}
                                    />
                                    <GridColumn
                                        key={"payer"}
                                        field={"payer"}
                                        title={"Payer"}
                                        width={defaultColumnWidth}
                                    />
                                    <GridColumn
                                        key={"bin"}
                                        field={"bin"}
                                        title={"BIN"}
                                        width={defaultColumnWidth}
                                    />
                                    <GridColumn
                                        key={"adjudicatedAmount"}
                                        field={"adjudicatedAmount"}
                                        title={"Adj. Amount"}
                                        width={defaultColumnWidth}
                                        cells={{ data: MoneyCell }}
                                    />
                                    <GridColumn
                                        key={"paidAmount"}
                                        field={"paidAmount"}
                                        title={"Paid Amount"}
                                        width={defaultColumnWidth}
                                        cells={{ data: MoneyCell }}
                                    />
                                    <GridColumn
                                        key={"clid"}
                                        field={"clid"}
                                        title={"Claim ID"}
                                        width={defaultColumnWidth}
                                    />
                                    <GridColumn
                                        key={"associatedChecks"}
                                        field={"associatedChecks"}
                                        title={"Associated Checks"}
                                        width={defaultColumnWidth}
                                    />
                                    <GridColumn
                                        key={"pmid"}
                                        field={"pmid"}
                                        title={"PMID"}
                                        width={defaultColumnWidth}
                                    />
                                    <GridColumn
                                        key={"adjustedBy"}
                                        field={"adjustedBy"}
                                        title={"Adjusted By"}
                                        width={defaultColumnWidth}
                                    />
                                    <GridColumn
                                        field=""
                                        title="History"
                                        filterable={false}
                                        sortable={false}
                                        groupable={false}
                                        width={DefaultNoFilterColumnWidth()}
                                        cells={{ data: HistoryCell }}
                                    />
                                </SharedMainSelectableClientGrid>
                            </Col>
                            :
                            null
                        }
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <h4>Payment Listing</h4>
                        {mainGridClientGridApiParams ?
                            <Col className="tight-grid multi-line-filter">
                                <SharedMainSelectableClientGrid
                                    ref={paymentListingGridRef}
                                    styleOverride={{ height: "510px" }}
                                    // styleOverride={{ height: "1200px" }}
                                    apiGetEndpoint={"api/AdminPaymentMatching/GetPaymentsForMatching"}
                                    apiParameters={mainGridClientGridApiParams}
                                    useQueryOptions={detailsGridUseQueryOptions}
                                    //NOTE: If isSelectable is true but no dataItemKey is selected, then everything is selected
                                    isSelectable={true}
                                    dataItemKey={"pyid"}
                                    onDataRetrievedProcessor={requestsDataProcessor}
                                >
                                    <GridColumn
                                        key={"ncpdp"}
                                        field={"ncpdp"}
                                        title={"NCPDP"}
                                        width={defaultColumnWidth}
                                    />
                                    <GridColumn
                                        key={"rxNumber"}
                                        field={"rxNumber"}
                                        title={"Rx #"}
                                        width={defaultColumnWidth}
                                    />
                                    <GridColumn
                                        key={"serviceDate"}
                                        field={"serviceDate"}
                                        title={"Service Date"}
                                        width={defaultWideColumnWidth}
                                        filter="date"
                                        format="{0:d}"
                                    />
                                    <GridColumn
                                        key={"pmid"}
                                        field={"pmid"}
                                        title={"PMID"}
                                        width={defaultColumnWidth}
                                    />
                                    <GridColumn
                                        key={"pyid"}
                                        field={"pyid"}
                                        title={"PYID"}
                                        width={defaultColumnWidth}
                                    />
                                    <GridColumn
                                        key={"payer"}
                                        field={"payer"}
                                        title={"Payer"}
                                        width={defaultColumnWidth}
                                    />
                                    <GridColumn
                                        key={"checkDate"}
                                        field={"checkDate"}
                                        title={"Check Date"}
                                        width={defaultWideColumnWidth}
                                        filter="date"
                                        format="{0:d}"
                                    />
                                    <GridColumn
                                        key={"checkNumber"}
                                        field={"checkNumber"}
                                        title={"Check Number"}
                                        width={defaultColumnWidth}
                                    />
                                    <GridColumn
                                        key={"archiveDate"}
                                        field={"archiveDate"}
                                        title={"Archive Date"}
                                        width={defaultWideColumnWidth}
                                        filter="date"
                                        format="{0:d}"
                                    />
                                    <GridColumn
                                        key={"paymentType"}
                                        field={"paymentType"}
                                        title={"Payment Type"}
                                        width={defaultColumnWidth}
                                    />
                                    <GridColumn
                                        key={"amount"}
                                        field={"amount"}
                                        title={"Amount"}
                                        width={defaultColumnWidth}
                                        cells={{ data: MoneyCell }}
                                    />
                                    <GridColumn
                                        key={"claimId"}
                                        field={"claimId"}
                                        title={"Claim ID"}
                                        width={defaultColumnWidth}
                                    />
                                    <GridColumn
                                        key={"claimId"}
                                        field={"claimId2"}
                                        title={"Claim ID2"}
                                        width={defaultColumnWidth}
                                    />
                                    <GridColumn
                                        field=""
                                        title="History"
                                        filterable={false}
                                        sortable={false}
                                        groupable={false}
                                        width={DefaultNoFilterColumnWidth()}
                                        cells={{ data: HistoryCell }}
                                    />
                                </SharedMainSelectableClientGrid>
                            </Col>
                            :
                            null
                        }
                    </Col>
                </Row>
                {
                    mainGridClientGridApiParams ?
                        <Row>
                            <Col md={10} />
                            <Col md={1}>
                                <Button className="inline-show-result-button" size="sm" onClick={handleUnmatchClick}>
                                    Unmatch
                                </Button>
                            </Col>
                            <Col md={1}>
                                <Button className="inline-show-result-button" size="sm" onClick={handleMatchClick}>
                                    Match
                                </Button>
                            </Col>
                        </Row>
                        :
                        null
                }
            </div>

            <DetailsModal
                title="Matching History"
                show={showHistoryModal}
                handleClose={() => {
                    return (
                        setShowHistoryModal(false),
                        setMatchingHistoryDetailsGridApiParams(null),
                        setMatchingHistoryDetailsGridApiEndpoint(null),
                        // setSelectedDetailsState({}),
                        // setSummaryDetailsGridData(null),
                        _container.current.focus()
                    );
                }}
            >
                <ZIndexContext.Provider value={10003}>
                    <Row>
                        <Col className="tight-grid multi-line-filter">
                            {matchingHistoryDetailsGridBasedOnClaimOrPayment()}
                        </Col>
                    </Row>
                </ZIndexContext.Provider>
            </DetailsModal>
        </div>
    );

}
